'use client';
import React, { useEffect, useRef, useState } from 'react';
import SortAndResult from './filters/sort-and-result';
import useWatch from '@/hooks/useWatch';
import useSearchParams from '@/hooks/useSearchParams';
import useSWRMutation from 'swr/mutation';
import { getProductsByCategoryIdV2 } from '@/services/category';
import ProductList from './product-list';
import Spinner from '../icons/Spinner';
import { useLocale } from 'next-intl';
import { useSWRConfig } from 'swr';
import useSetProductsFromCache from '@/hooks/useSetProductsFromCache';
function ProductWrapper({
  initialProducts,
  totalProducts: initialTotalProducts,
  filterCodes,
  categoryId,
  isMobile,
}) {
  const lastProductRef = useRef(null);
  const observerRef = useRef(null);
  const locale = useLocale();

  const { getFilterObj } = useSearchParams();

  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 32;

  const [products, setProducts] = useState(initialProducts || []);
  const [totalProducts, setTotalProducts] = useState(initialTotalProducts || 0);

  const [isGetProductsFromCache, setIsGetProductsFromCache] = useState(true);

  const [hasMore, setHasMore] = useState(initialTotalProducts > pageSize);
  
  const { cache, mutate } = useSWRConfig();

  const url_cach = `/api/category/pages/getMoreProducts?pageIdx=${currentPage}&pageSize=${pageSize}&categoryID=${categoryId}&${Object.entries(getFilterObj).flatMap(([key, value]) => value.split(',').map((val) => `${key}=${val}`))}&${Object.entries(filterCodes).flatMap(([key, value]) => value.split(',').map((val) => `${key}=${val}`))}`
  const checkOnKey = '/api/category/pages/getMoreProducts'
  
  const { trigger, isMutating } = useSWRMutation(
    url_cach,
    async () => {
      const transformed = Object.entries(getFilterObj).flatMap(([key, value]) =>
        value.split(',').map((val) => `${key}=${val}`)
      );

      const response = await getProductsByCategoryIdV2({
        categoryId,
        pageSize,
        pageNo: currentPage,
        query: transformed?.join('&'),
        getFullResponse: false,
        locale,
      });
      return response;
    },
    {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      onError: () => {},
      onSuccess: (data) => {
        if (data?.data?.data?.products?.length > 0) {
          setProducts((prevProducts) => {
            const new_products = [
              ...(currentPage ? [...prevProducts] : []),
              ...data?.data?.data?.products,
            ];

            setHasMore(new_products?.length < data?.data?.data?.totalProducts);
            
            // set coming products in the cache with unique key
            mutate(url_cach, {
              products: data?.data?.data?.products,
              totalProducts: data?.data?.data?.totalProducts,
              currentPage,
              hasMore: new_products?.length < data?.data?.data?.totalProducts,
            }, false); // false = don't revalidate

            return new_products;
          });

          setTotalProducts(data?.data?.data?.totalProducts);
        } else {
          setHasMore(false);
          setProducts([]);
          setTotalProducts(0);
        }
      },
    }
  );

  useWatch(() => {
    setCurrentPage(0);
    // trigger if the current page is is 0 because the send effect will not
    currentPage === 0 && trigger();
  }, [JSON.stringify(getFilterObj)]);

  useWatch(() => {
    !isGetProductsFromCache && hasMore && trigger();
  }, [currentPage]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (first.isIntersecting && hasMore && !isMutating) {
          setIsGetProductsFromCache(false)
          setCurrentPage(currentPage + 1);
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.3,
      }
    );

    if (lastProductRef.current) {
      observerRef.current.observe(lastProductRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isMutating, products]);

  useSetProductsFromCache({checkOnKey, cache, categoryId, products, setProducts, setCurrentPage, setHasMore})

  return (
    <div className='md:col-span-full lg:col-span-3'>
      {!isMobile && (
        <SortAndResult
          productsCount={products?.length}
          totalProductsCount={totalProducts}
        />
      )}
      <ProductList
        categoryId={categoryId}
        products={products}
        lastProductRef={lastProductRef}
      />
      {isMutating && (
        <div className='mt-4 flex justify-center'>
          <Spinner width={50} />
        </div>
      )}
    </div>
  );
}

export default ProductWrapper;
